"use client";

import { useEffect, useRef, useState } from "react";
import { FiMonitor, FiSmartphone } from "react-icons/fi";
import { type GamePreviewMode } from "./gamePreviewConstants";
import { buildGamePreviewSrc } from "./gamePreviewUrl";

export type { GamePreviewMode } from "./gamePreviewConstants";

export {
    MOBILE_PREVIEW_WIDTH,
    MOBILE_PREVIEW_HEIGHT,
} from "./gamePreviewConstants";

/** เปิดใช้ปุ่มสลับ Desktop / Mobile เมื่อพร้อม */
export const SHOW_PREVIEW_MODE_TOGGLE = false;

type GamePreviewModeToggleProps = {
    mode: GamePreviewMode;
    onChange: (mode: GamePreviewMode) => void;
    className?: string;
};

export function GamePreviewModeToggle({ mode, onChange, className = "" }: GamePreviewModeToggleProps) {
    if (!SHOW_PREVIEW_MODE_TOGGLE) {
        return null;
    }

    const base =
        "inline-flex cursor-pointer items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors";
    const active = "bg-[#22A8DF] text-white";
    const inactive = "bg-neutral-100 text-neutral-600 hover:bg-neutral-200";

    return (
        <div
            role="group"
            aria-label="เลือกรูปแบบการพรีวิว"
            className={`inline-flex overflow-hidden rounded-lg border border-neutral-200 p-0.5 ${className}`}
        >
            <button
                type="button"
                aria-pressed={mode === "desktop"}
                onClick={() => onChange("desktop")}
                className={`${base} ${mode === "desktop" ? active : inactive}`}
            >
                <FiMonitor className="text-base" />
                Desktop
            </button>
            <button
                type="button"
                aria-pressed={mode === "mobile"}
                onClick={() => onChange("mobile")}
                className={`${base} ${mode === "mobile" ? active : inactive}`}
            >
                <FiSmartphone className="text-base" />
                Mobile
            </button>
        </div>
    );
}

type GamePreviewIframeProps = {
    src: string;
    iframeTitle: string;
    reloadKey: string;
    className?: string;
};

function GamePreviewIframe({ src, iframeTitle, reloadKey, className = "" }: GamePreviewIframeProps) {
    return (
        <iframe
            key={reloadKey}
            src={src}
            title={iframeTitle}
            allow="fullscreen"
            allowFullScreen
            className={`block h-full w-full border-0 ${className}`}
        />
    );
}

type GamePreviewViewportProps = {
    src: string;
    iframeTitle: string;
    className?: string;
};

export function GamePreviewViewport({ src, iframeTitle, className = "" }: GamePreviewViewportProps) {
    const [reloadToken, setReloadToken] = useState(0);
    const skipInitialReload = useRef(true);

    useEffect(() => {
        if (skipInitialReload.current) {
            skipInitialReload.current = false;
            return;
        }
        setReloadToken((prev) => prev + 1);
    }, [src]);

    const iframeSrc = buildGamePreviewSrc(src, "desktop", reloadToken);
    const reloadKey = `desktop-${reloadToken}-${iframeSrc}`;

    return (
        <GamePreviewIframe
            src={iframeSrc}
            iframeTitle={iframeTitle}
            reloadKey={reloadKey}
            className={className}
        />
    );
}

type GamePreviewInlineProps = {
    src: string;
    iframeTitle: string;
    frameClassName?: string;
};

export function GamePreviewInline({ src, iframeTitle, frameClassName = "h-[70vh]" }: GamePreviewInlineProps) {
    return (
        <div className={`overflow-hidden border border-neutral-200 ${frameClassName}`}>
            <GamePreviewViewport src={src} iframeTitle={iframeTitle} className="h-full min-h-0" />
        </div>
    );
}
